export type ViewportOrientation = "portrait" | "landscape";

export type CameraCaptureViewport = {
  width: number;
  height: number;
  orientation: ViewportOrientation;
};

/** Landscape sensor fallback (width / height) before stream metadata is ready. */
export const CAMERA_PREVIEW_FALLBACK_ASPECT_LANDSCAPE = 4 / 3;

/** Portrait fallback (width / height): taller than wide. */
export const CAMERA_PREVIEW_FALLBACK_ASPECT_PORTRAIT = 3 / 4;

export function readViewportOrientation(width: number, height: number): ViewportOrientation {
  if (width > 0 && height > 0) {
    return height > width ? "portrait" : "landscape";
  }
  return "portrait";
}

export function readViewportOrientationFromDevice(windowLike?: Window): ViewportOrientation | null {
  if (windowLike == null) return null;
  const screenType = windowLike.screen?.orientation?.type;
  if (typeof screenType === "string") {
    if (screenType.startsWith("portrait")) return "portrait";
    if (screenType.startsWith("landscape")) return "landscape";
  }
  if (typeof windowLike.matchMedia === "function") {
    if (windowLike.matchMedia("(orientation: portrait)").matches) return "portrait";
    if (windowLike.matchMedia("(orientation: landscape)").matches) return "landscape";
  }
  return null;
}

/**
 * Hub WebView box: prefer `visualViewport`, then `innerWidth` / `innerHeight`.
 * Orientation prefers `screen.orientation`, then aspect of the live viewport.
 */
export function readCameraCaptureViewport(windowLike?: Window): CameraCaptureViewport {
  if (windowLike == null) {
    return { width: 0, height: 0, orientation: "portrait" };
  }

  const visual = windowLike.visualViewport;
  const innerWidth = Math.round(windowLike.innerWidth);
  const innerHeight = Math.round(windowLike.innerHeight);
  let width = Math.round(visual?.width ?? innerWidth);
  let height = Math.round(visual?.height ?? innerHeight);

  // Modal overlays and browser chrome often shrink visualViewport without changing innerHeight.
  if (innerHeight > 0) {
    const collapsedExtreme = height < Math.min(320, Math.floor(innerHeight * 0.45));
    const collapsedPartial =
      innerHeight - height > 120 && height < Math.floor(innerHeight * 0.85);
    if (collapsedExtreme || collapsedPartial) {
      height = innerHeight;
      width = innerWidth;
    }
  }

  const orientation =
    readViewportOrientationFromDevice(windowLike) ?? readViewportOrientation(width, height);

  return {
    width,
    height,
    orientation,
  };
}

/**
 * Full-screen modal layout must use `innerHeight`, not `visualViewport.height`.
 * Browsers shrink visualViewport when a modal opens; that produced ~400px-tall cards
 * and a flex-squashed preview strip on Receive.
 */
export function readCameraCaptureViewportForModal(windowLike?: Window): CameraCaptureViewport {
  if (windowLike == null) {
    return { width: 0, height: 0, orientation: "portrait" };
  }
  const width = Math.round(windowLike.innerWidth);
  const height = Math.round(windowLike.innerHeight);
  const orientation =
    readViewportOrientationFromDevice(windowLike) ?? readViewportOrientation(width, height);
  return { width, height, orientation };
}

/** Container aspect for layout: desktop webcam uses a tall portrait box; object-cover crops the stream. */
export function cameraCaptureLayoutAspectRatio(input: {
  deviceKind: CameraCaptureDeviceKind;
  streamAspectRatio: number;
}): number {
  if (input.deviceKind === "desktop_webcam") {
    return CAMERA_PREVIEW_FALLBACK_ASPECT_PORTRAIT;
  }
  if (input.streamAspectRatio > 0) {
    return input.streamAspectRatio;
  }
  return fallbackCameraPreviewAspect("portrait");
}

/** Width of the hub `main` content column (matches operational page layout, not full window). */
export function readHubMainContentWidth(windowLike?: Window): number {
  const win =
    windowLike ?? (typeof globalThis !== "undefined" ? globalThis.window : undefined);
  if (typeof document !== "undefined") {
    const main = document.querySelector("main");
    if (main instanceof HTMLElement) {
      const rect = main.getBoundingClientRect();
      const measured = Math.max(main.clientWidth, Math.round(rect.width));
      if (measured > 0) {
        return measured;
      }
    }
  }
  if (win != null && win.innerWidth > 0) {
    return Math.round(win.innerWidth);
  }
  return readCameraCaptureViewportForModal(win).width;
}

/** Portrait width/height bounds (width ÷ height) for preview layout. */
export const CAMERA_PREVIEW_PORTRAIT_ASPECT_MIN = 0.55;
export const CAMERA_PREVIEW_PORTRAIT_ASPECT_MAX = 0.85;
/** Landscape width/height bounds for preview layout. */
export const CAMERA_PREVIEW_LANDSCAPE_ASPECT_MIN = 1.2;
export const CAMERA_PREVIEW_LANDSCAPE_ASPECT_MAX = 1.85;

function clampCameraPreviewAspect(
  aspectRatio: number,
  orientation: ViewportOrientation,
): number {
  if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return fallbackCameraPreviewAspect(orientation);
  }
  if (orientation === "portrait") {
    return Math.min(
      CAMERA_PREVIEW_PORTRAIT_ASPECT_MAX,
      Math.max(CAMERA_PREVIEW_PORTRAIT_ASPECT_MIN, aspectRatio),
    );
  }
  return Math.min(
    CAMERA_PREVIEW_LANDSCAPE_ASPECT_MAX,
    Math.max(CAMERA_PREVIEW_LANDSCAPE_ASPECT_MIN, aspectRatio),
  );
}

export function fallbackCameraPreviewAspect(orientation: ViewportOrientation): number {
  return orientation === "portrait"
    ? CAMERA_PREVIEW_FALLBACK_ASPECT_PORTRAIT
    : CAMERA_PREVIEW_FALLBACK_ASPECT_LANDSCAPE;
}

export function readCameraPreviewAspectRatio(width: number, height: number): number {
  if (width > 0 && height > 0) {
    return width / height;
  }
  return CAMERA_PREVIEW_FALLBACK_ASPECT_LANDSCAPE;
}

/**
 * Match preview aspect to how the operator is holding the device.
 * Rear cameras often report landscape sensor pixels even in portrait; swap when needed.
 */
export function normalizeCameraPreviewAspect(input: {
  videoWidth: number;
  videoHeight: number;
  orientation: ViewportOrientation;
}): number {
  if (input.videoWidth <= 0 || input.videoHeight <= 0) {
    return fallbackCameraPreviewAspect(input.orientation);
  }

  const rawAspect = input.videoWidth / input.videoHeight;
  const feedIsPortrait = input.videoHeight > input.videoWidth;
  const expectPortrait = input.orientation === "portrait";

  if (feedIsPortrait !== expectPortrait) {
    return clampCameraPreviewAspect(input.videoHeight / input.videoWidth, input.orientation);
  }

  return clampCameraPreviewAspect(rawAspect, input.orientation);
}

export const CAMERA_PREVIEW_MIN_WIDTH_PX = 280;
export const CAMERA_PREVIEW_MIN_HEIGHT_PX = 220;

/** Largest preview box that fits max bounds while preserving aspect ratio. */
export function fitCameraPreviewBox(input: {
  aspectRatio: number;
  maxWidth: number;
  maxHeight: number;
  minWidth?: number;
  minHeight?: number;
}): { width: number; height: number } {
  const aspectRatio =
    input.aspectRatio > 0 ? input.aspectRatio : CAMERA_PREVIEW_FALLBACK_ASPECT_LANDSCAPE;
  const maxWidth = Math.max(CAMERA_PREVIEW_MIN_WIDTH_PX, input.maxWidth);
  const maxHeight = Math.max(CAMERA_PREVIEW_MIN_HEIGHT_PX, input.maxHeight);
  const minWidth = Math.min(maxWidth, Math.max(1, input.minWidth ?? CAMERA_PREVIEW_MIN_WIDTH_PX));
  const minHeight = Math.min(maxHeight, Math.max(1, input.minHeight ?? CAMERA_PREVIEW_MIN_HEIGHT_PX));

  let width = maxWidth;
  let height = width / aspectRatio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  width = Math.max(minWidth, Math.min(maxWidth, Math.floor(width)));
  height = Math.max(minHeight, Math.min(maxHeight, Math.floor(height)));

  return { width, height };
}

/**
 * Source rectangle on the video that matches CSS `object-cover` for a container aspect.
 * Used so captured JPEG matches what the operator saw in the preview box.
 */
export function computeObjectCoverSourceRect(input: {
  videoWidth: number;
  videoHeight: number;
  containerWidth: number;
  containerHeight: number;
}): { sx: number; sy: number; sw: number; sh: number } {
  const videoWidth = Math.max(1, Math.floor(input.videoWidth));
  const videoHeight = Math.max(1, Math.floor(input.videoHeight));
  const containerWidth = Math.max(1, Math.floor(input.containerWidth));
  const containerHeight = Math.max(1, Math.floor(input.containerHeight));

  const videoAspect = videoWidth / videoHeight;
  const containerAspect = containerWidth / containerHeight;

  if (videoAspect > containerAspect) {
    const sw = videoHeight * containerAspect;
    const sx = (videoWidth - sw) / 2;
    return {
      sx: Math.round(sx),
      sy: 0,
      sw: Math.round(sw),
      sh: videoHeight,
    };
  }

  const sh = videoWidth / containerAspect;
  const sy = (videoHeight - sh) / 2;
  return {
    sx: 0,
    sy: Math.round(sy),
    sw: videoWidth,
    sh: Math.round(sh),
  };
}

/** Tablet-sized viewport (includes devtools device mode without touch). */
export function isCompactCameraCaptureViewport(width: number): boolean {
  return width > 0 && width <= 1280;
}

export type CameraCaptureDeviceKind = "floor_tablet" | "touch_kiosk" | "desktop_webcam";

export function readCameraCaptureDeviceKind(input: {
  floorTablet: boolean;
  wideTouchKiosk: boolean;
  touchCoarse: boolean;
  touchHoverNone: boolean;
}): CameraCaptureDeviceKind {
  if (input.floorTablet) return "floor_tablet";
  if (input.wideTouchKiosk || input.touchCoarse || input.touchHoverNone) return "touch_kiosk";
  return "desktop_webcam";
}

/** Desktop mouse/keyboard: grow the preview to use the modal, not a fixed small box. */
export function shouldFillCameraCaptureViewport(input: {
  viewportWidth: number;
  floorTablet: boolean;
  wideTouchKiosk: boolean;
  deviceKind: CameraCaptureDeviceKind;
}): boolean {
  if (input.floorTablet || input.wideTouchKiosk) return true;
  if (input.deviceKind === "desktop_webcam") return true;
  return isCompactCameraCaptureViewport(input.viewportWidth);
}

/** Preview max bounds from the live viewport, orientation, and modal chrome. */
export function cameraPreviewBounds(input: {
  viewportWidth: number;
  viewportHeight: number;
  floorTablet: boolean;
  wideTouchKiosk?: boolean;
  compactViewport?: boolean;
  orientation: ViewportOrientation;
}): { maxWidth: number; maxHeight: number } {
  const wideTouchKiosk = input.wideTouchKiosk === true;
  const compactViewport =
    input.compactViewport === true || isCompactCameraCaptureViewport(input.viewportWidth);
  const touchKiosk = input.floorTablet || wideTouchKiosk || compactViewport;
  const horizontalInset = touchKiosk ? 16 : 24;
  const verticalChrome = touchKiosk ? 128 : 160;

  const widthBudget = Math.max(CAMERA_PREVIEW_MIN_WIDTH_PX, input.viewportWidth - horizontalInset * 2);
  const heightBudget = Math.max(
    CAMERA_PREVIEW_MIN_HEIGHT_PX,
    input.viewportHeight - verticalChrome,
  );

  if (input.orientation === "portrait") {
    const portraitHeightShare = touchKiosk ? 0.94 : 0.84;
    return {
      maxWidth: Math.floor(widthBudget),
      maxHeight: Math.floor(Math.min(heightBudget * portraitHeightShare, heightBudget)),
    };
  }

  const landscapeHeightShare = touchKiosk ? 0.86 : 0.78;
  return {
    maxWidth: Math.floor(widthBudget),
    maxHeight: Math.floor(Math.min(heightBudget * landscapeHeightShare, heightBudget)),
  };
}

/** Modal chrome below the live preview (header, hint, buttons, card padding). */
export function cameraCaptureModalChromeHeight(
  deviceKind: CameraCaptureDeviceKind,
  options?: { hasHint?: boolean },
): number {
  const hintExtra = options?.hasHint === true ? 52 : 0;
  if (deviceKind === "desktop_webcam") return 132 + hintExtra;
  if (deviceKind === "floor_tablet") return 128 + hintExtra;
  return 140 + hintExtra;
}

const CAMERA_CAPTURE_OVERLAY_PADDING_PX = 16;
const CAMERA_CAPTURE_CARD_BODY_PADDING_X_PX = 48;
const CAMERA_CAPTURE_CARD_BODY_PADDING_Y_PX = 32;

/** Largest card height the modal may use inside the overlay padding. */
export function cameraCaptureMaxCardHeight(viewportHeight: number): number {
  return Math.max(
    CAMERA_PREVIEW_MIN_HEIGHT_PX + 160,
    viewportHeight - CAMERA_CAPTURE_OVERLAY_PADDING_PX * 2,
  );
}

/**
 * Size the live preview from page column width, stream aspect ratio, and viewport height.
 * The modal applies these as CSS width + aspect-ratio + max-height so the box always
 * matches the camera feed (no tall black letterboxing around a landscape stream).
 */
export function computeCameraPreviewContainerLayout(input: {
  pageContentWidth: number;
  viewportHeight: number;
  aspectRatio: number;
  deviceKind: CameraCaptureDeviceKind;
  hasHint?: boolean;
}): {
  maxWidth: number;
  aspectRatio: number;
  maxHeight: number;
  expectedWidth: number;
  expectedHeight: number;
} {
  const aspectRatio =
    input.aspectRatio > 0 ? input.aspectRatio : fallbackCameraPreviewAspect("portrait");
  const maxWidth = Math.max(
    CAMERA_PREVIEW_MIN_WIDTH_PX,
    Math.floor(Math.max(CAMERA_PREVIEW_MIN_WIDTH_PX, input.pageContentWidth))
      - CAMERA_CAPTURE_CARD_BODY_PADDING_X_PX,
  );
  const chromeHeight = cameraCaptureModalChromeHeight(input.deviceKind, {
    hasHint: input.hasHint,
  });
  const maxHeight = Math.max(
    CAMERA_PREVIEW_MIN_HEIGHT_PX,
    cameraCaptureMaxCardHeight(input.viewportHeight) - chromeHeight - CAMERA_CAPTURE_CARD_BODY_PADDING_Y_PX,
  );

  let expectedWidth = maxWidth;
  let expectedHeight = expectedWidth / aspectRatio;
  if (expectedHeight > maxHeight) {
    expectedHeight = maxHeight;
    expectedWidth = expectedHeight * aspectRatio;
  }

  expectedWidth = Math.max(
    CAMERA_PREVIEW_MIN_WIDTH_PX,
    Math.min(maxWidth, Math.floor(expectedWidth)),
  );
  expectedHeight = Math.max(
    CAMERA_PREVIEW_MIN_HEIGHT_PX,
    Math.min(maxHeight, Math.floor(expectedHeight)),
  );

  return {
    maxWidth,
    aspectRatio,
    maxHeight,
    expectedWidth,
    expectedHeight,
  };
}

/**
 * Size the fill-layout card and preview from the hub page column and viewport height.
 */
export function computeFilledCameraPreviewLayout(input: {
  pageContentWidth: number;
  viewportHeight: number;
  aspectRatio: number;
  deviceKind: CameraCaptureDeviceKind;
  hasHint?: boolean;
}): {
  cardWidth: number;
  cardHeight: number;
  previewWidth: number;
  previewHeight: number;
} {
  const chromeHeight = cameraCaptureModalChromeHeight(input.deviceKind, {
    hasHint: input.hasHint,
  });
  const preview = computeCameraPreviewContainerLayout(input);
  const cardWidth = Math.floor(Math.max(CAMERA_PREVIEW_MIN_WIDTH_PX, input.pageContentWidth));

  return {
    cardWidth,
    cardHeight: Math.floor(
      preview.expectedHeight + chromeHeight + CAMERA_CAPTURE_CARD_BODY_PADDING_Y_PX,
    ),
    previewWidth: preview.expectedWidth,
    previewHeight: preview.expectedHeight,
  };
}

export function buildCameraVideoConstraints(
  orientation: ViewportOrientation,
  deviceKind: CameraCaptureDeviceKind = "touch_kiosk",
): MediaTrackConstraints {
  if (deviceKind === "desktop_webcam") {
    return {
      facingMode: { ideal: "user" },
      width: { ideal: 1280, max: 1920 },
      height: { ideal: 720, max: 1080 },
      frameRate: { ideal: 30, max: 30 },
    };
  }

  const portrait = orientation === "portrait";
  return {
    facingMode: { ideal: "environment" },
    width: { ideal: portrait ? 960 : 1280, max: portrait ? 1280 : 1920 },
    height: { ideal: portrait ? 1280 : 720, max: portrait ? 1920 : 1080 },
    aspectRatio: { ideal: portrait ? 3 / 4 : 4 / 3 },
    frameRate: { ideal: 30, max: 30 },
  };
}
